import React from "react";
import { Box, TextField, FormControl, Typography, MenuItem, Select, InputLabel, Autocomplete } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate } from "react-router-dom";
import { useCreateLoteMutation } from "../../../services/loteService";
import { lote_type } from "../../../constants/enums";
import { harvest_tag } from "../../../constants/enums";
import { useGetTemplatesQuery } from "../../../services/templateService";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SuccessButton from "../../../components/SuccessButton";
import CancelButton from "../../../components/CancelButton";
import dayjs from 'dayjs'; // Import dayjs

const CreateLote = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();

  const [createLote] = useCreateLoteMutation();
  const { data: templates, error, isLoading } = useGetTemplatesQuery({
    refetchOnMountOrArgChange: true,
  });

  const handleFormSubmit = async (lote, { resetForm }) => {
    const newLote = lote;
    newLote.sowing_date = newLote.sowing_date ? newLote.sowing_date.format('YYYY-MM-DD') : null;
    try {
      await createLote(newLote).unwrap();
      showAlert("Lote creado!", "success");
      resetForm();
      navigate('/lotes');
    } catch (error) {
      console.error("Error creating lote:", error);
      showAlert(error.message, "error");
    }
  };

  const initialValues = {
    name: "",
    type: "",
    harvest_tag: "",
    number: "",
    sowing_date: null, // Initialize as null for DatePicker
    template_id: "" // Initialize as an empty string or null
  };

  const loteSchema = yup.object().shape({
    name: yup.string().required("Requerido"),
    type: yup.string().required("Requerido"),
    harvest_tag: yup.string().required("Requerido"),
    number: yup.string().required("Requerido"),
    sowing_date: yup
    .date()
    .nullable()
    .typeError("Ingrese una fecha válida") // Custom error message
    .required("Requerido"),
    template_id: yup.string().required("Requerido") // Validate as a string
  });

  return (
    <Box m="20px">
      <Header title="Lotes" subtitle="Crear Lote" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={loteSchema}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Nombre del Lote"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                name="name"
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Número"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.number}
                name="number"
                error={!!touched.number && !!errors.number}
                helperText={touched.number && errors.number}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="type-label">Tipo de Semilla</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.type && !!errors.type}
                >
                  {lote_type.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                {touched.type && errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="harvest_tag-label">Cosecha</InputLabel>
                <Select
                  labelId="harvest_tag-label"
                  id="harvest_tag"
                  name="harvest_tag"
                  value={values.harvest_tag}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.harvest_tag && !!errors.harvest_tag}
                >
                  {harvest_tag.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                {touched.harvest_tag && errors.harvest_tag && (
                  <Typography variant="caption" color="error">
                    {errors.harvest_tag}
                  </Typography>
                )}
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de Siembra"
                  value={values.sowing_date ? dayjs(values.sowing_date) : null} // Convert to Day.js object
                  onChange={(newValue) => setFieldValue("sowing_date", newValue)} // Set as Day.js object
                  format="DD/MM/YYYY" // Set user-friendly format
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      error: !!touched.sowing_date && !!errors.sowing_date,
                      helperText: touched.sowing_date && errors.sowing_date,
                      sx: { gridColumn: "span 1" }
                    }
                  }}
                />
            </LocalizationProvider>
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <Autocomplete
                  options={templates?.records || []}
                  getOptionLabel={(option) => option.name} // Display the template name
                  value={
                    templates?.records.find((template) => template.id === values.template_id) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue("template_id", newValue ? newValue.id : ""); // Store only the ID
                  }}
                  onBlur={handleBlur}
                  loading={isLoading}
                  noOptionsText={error ? "Error al cargar planes" : "No se encontraron planes"}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plan de aplicaciones"
                      variant="filled"
                      error={!!touched.template_id && !!errors.template_id}
                      helperText={touched.template_id && errors.template_id}
                    />
                  )}
                />
              </FormControl>
            </Box>

            <Box
              display="flex"
              justifyContent="end"
              gap="4px"
              mt="1%"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/lotes')}/>
              <SuccessButton label="Crear Lote" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateLote;